import { useState, useEffect, useCallback, useRef } from 'react';
import { tasksApi, categoriesApi } from '../api';
import { offlineStorage } from '../utils/offlineStorage';
import { useAuth } from '../contexts/AuthContext';

export function useTasks(params = {}) {
  const { user } = useAuth();
  const isOnline = !!user;

  const [tasks,      setTasks]      = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);

  const fetchingRef = useRef(false);
  const paramsRef   = useRef(params);
  paramsRef.current = params;

  const unwrap = (res, fb) => {
    if (!res?.data) return fb;
    if (res.data?.data !== undefined) return res.data.data;
    return Array.isArray(res.data) ? res.data : fb;
  };

  const unwrapOne = (res) => {
    if (!res?.data) return null;
    if (res.data?.data !== undefined) return res.data.data;
    return res.data ?? null;
  };

  const fetchAll = useCallback(async () => {
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      if (isOnline) {
        const [tasksRes, catsRes] = await Promise.all([
          tasksApi.list(paramsRef.current),
          categoriesApi.list(),
        ]);
        const tasksData = unwrap(tasksRes, []);
        const catsData  = unwrap(catsRes,  []);
        setTasks(Array.isArray(tasksData) ? tasksData : []);
        setCategories(Array.isArray(catsData) ? catsData : []);
      } else {
        setTasks(offlineStorage.getAll());
        setCategories([]);
      }
    } catch (e) {
      setError(e);
      setTasks(offlineStorage.getAll());
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [isOnline]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Toggle ────────────────────────────────────────────────────────────
  const toggleTask = useCallback(async (id) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    if (task._completing) return; // prevent double-tap
    const isRecurring = !!(task.recurrence_type && task.recurrence_type !== 'none');

    if (isOnline) {
      // Optimistic update
      if (!isRecurring) {
        setTasks(prev => prev.map(t =>
          t.id === id ? { ...t, done: !t.done, completed_at: !t.done ? new Date().toISOString() : null } : t
        ));
      } else {
        // For recurring: show spinner, don't optimistically set done
        setTasks(prev => prev.map(t => t.id === id ? { ...t, _completing: true } : t));
      }
      try {
        const res = await tasksApi.complete(id);
        // Backend returns task fields + optionally _recurrence_advanced:true at top level
        // unwrapOne handles both wrapped {data:{...}} and plain {...} responses
        const raw = unwrapOne(res);
        if (!raw) { setTasks(prev => prev.map(t => t.id === id ? { ...task, _completing: false } : t)); return; }
        // Strip internal flags, keep all task fields including next_due_date
        const { _recurrence_advanced, ...updated } = raw;
        setTasks(prev => prev.map(t =>
          t.id === id ? { ...updated, _completing: false, _recurrence_advanced: undefined } : t
        ));
      } catch {
        // Rollback on error
        setTasks(prev => prev.map(t => t.id === id ? { ...task, _completing: false } : t));
      }
    } else {
      const updated = offlineStorage.toggle(id);
      if (updated) setTasks(prev => prev.map(t => t.id === id ? updated : t));
    }
  }, [isOnline, tasks]);

  // ── Add ───────────────────────────────────────────────────────────────
  const addTask = useCallback(async (payload) => {
    if (isOnline) {
      const res = await tasksApi.create(payload);
      const created = unwrapOne(res) ?? payload;
      setTasks(prev => [created, ...prev]);
      return created;
    } else {
      const t = offlineStorage.add(payload);
      setTasks(prev => [t, ...prev]);
      return t;
    }
  }, [isOnline]);

  // ── Update ────────────────────────────────────────────────────────────
  const updateTask = useCallback(async (id, payload) => {
    if (isOnline) {
      const res = await tasksApi.update(id, payload);
      const updated = unwrapOne(res) ?? payload;
      setTasks(prev => prev.map(t => t.id === id ? updated : t));
      return updated;
    } else {
      const updated = offlineStorage.update(id, payload);
      setTasks(prev => prev.map(t => t.id === id ? updated : t));
      return updated;
    }
  }, [isOnline]);

  // ── Delete ────────────────────────────────────────────────────────────
  const deleteTask = useCallback(async (id) => {
    setTasks(prev => prev.filter(t => t.id !== id));
    if (isOnline) {
      try { await tasksApi.remove(id); }
      catch { fetchAll(); }
    } else {
      offlineStorage.remove(id);
    }
  }, [isOnline, fetchAll]);

  // ── Reorder ───────────────────────────────────────────────────────────
  // CRITICAL FIX: reordered is only the VISIBLE PENDING SUBSET.
  // We must merge it back into the full tasks array, preserving all other tasks.
  const reorderTasks = useCallback(async (reordered) => {
    setTasks(prev => {
      const orderMap = new Map(reordered.map((t, i) => [t.id, i]));
      const maxNew   = reordered.length;
      let offset     = 0;
      const updated  = prev.map(t => {
        if (orderMap.has(t.id)) return { ...t, sort_order: orderMap.get(t.id) };
        return { ...t, sort_order: maxNew + (offset++) };
      });
      // Re-sort the full array by sort_order so filters preserve drag order
      return [...updated].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    });

    if (!isOnline) return;
    try {
      await tasksApi.reorder(
        reordered.map((t, i) => ({ id: t.id, sort_order: i }))
      );
    } catch {
      fetchAll();
    }
  }, [isOnline, fetchAll]);

  return {
    tasks,
    categories,
    loading,
    error,
    isOnline,
    refetch: fetchAll,
    setTasks,
    toggleTask,
    addTask,
    updateTask,
    deleteTask,
    removeTask: deleteTask,
    reorderTasks,
  };
}